import { IconColor, IconID } from '@common/Icon/Icon';
import { TooltipIcon } from '@common/TooltipIcon/TooltipIcon';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import { getChainflexOverstockItems } from '@libs/catalog-service';
import { useRouter } from 'next/router';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import * as Sentry from '@sentry/nextjs';
import { useSessionStore } from '@hooks/states/useSessionStore';
import { getRemoteConfig } from '@libs/abby/abby';

export type OverstockPrice = {
  amount: number;
  currency: string;
};

export type OverstockItemProps = {
  articleNo: string;
  storageLocation: string;
  chainflexMeterLength: number;
  unitPrice: OverstockPrice;
  totalPrice: OverstockPrice;
};

export type ChainflexOverstockSaleLocalesConfig = {
  enabledLocales: string[];
  alternateStockData: { [key: string]: string }[];
};

const fetchOverstockItems = async (
  productId: string,
  locale: string,
  setOverstockItems: Dispatch<SetStateAction<OverstockItemProps[]>>
) => {
  try {
    const response = await getChainflexOverstockItems(productId, locale);
    const data = JSON.parse(response) as OverstockItemProps[];
    setOverstockItems(data.slice(0, 10));
  } catch (error) {
    Sentry.captureException(new Error(`Could not get chainflex overstock items - ${error as string}`));
  }
};

export const OverstockItem = ({ item, selected }: { item: OverstockItemProps; selected?: boolean }) => {
  const setSelectedChainflexOverstockItem = useProductPageStore(state => state.setSelectedChainflexOverstockItem);
  const resetSelectedChainflexOverstockItem = useProductPageStore(state => state.resetSelectedChainflexOverstockItem);

  const handleClick = () => {
    if (selected) {
      resetSelectedChainflexOverstockItem();
    } else {
      setSelectedChainflexOverstockItem(item);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`cursor-pointer shrink-0 h-10 text-[10px] rounded-lg select-none ${
        selected
          ? 'bg-igus-primary text-white shadow-[0px_3.73px_13.05px_0px_#00000040]'
          : 'bg-white shadow-[0px_0.97px_2.92px_0px_#00000040]'
      } hover:bg-[#FDF2E5] p-2 font-[400] hover:shadow-[0px_3.73px_13.05px_0px_#00000040]`}
    >
      <span className="font-bold text-center align-middle">
        {item.chainflexMeterLength} m{selected && <span className="ml-3">&#x2715;</span>}
      </span>
    </div>
  );
};

export const ChainflexOverstockSale = () => {
  const { t } = useTranslation('product/buyBox');
  const currentProductId = useProductPageStore(state => state.currentProduct.id);
  const selectedChainflexOverstockItem = useProductPageStore(state => state.selectedChainflexOverstockItem);
  const resetSelectedChainflexOverstockItem = useProductPageStore(state => state.resetSelectedChainflexOverstockItem);
  const [overstockItems, setOverstockItems] = useState<OverstockItemProps[]>([]);
  const session = useSessionStore(state => state.session);
  const { locale } = useRouter();
  const { alternateStockData } = getRemoteConfig<'chainflexOverstockSaleLocales', 'JSON'>(
    'chainflexOverstockSaleLocales'
  ) as ChainflexOverstockSaleLocalesConfig;

  const overstockSaleLocale = alternateStockData.find(data => data[locale])?.[locale] || locale;

  useEffect(() => {
    resetSelectedChainflexOverstockItem();

    void fetchOverstockItems(currentProductId, overstockSaleLocale, setOverstockItems);
  }, [
    currentProductId,
    overstockSaleLocale,
    resetSelectedChainflexOverstockItem,
    session?.cartArticleCount,
    setOverstockItems,
  ]);

  if (overstockItems.length === 0) {
    return null;
  }

  return (
    <div data-testid="chainflex-overstock-sale">
      <div className="my-4">
        <span className="font-bold underline">{t('BUY_BOX.CHAINFLEX_OVERSTOCK_SALE.TITLE')}:</span>&ensp;
        <TooltipIcon
          id={IconID.INFO_CIRCLE_MODERN}
          color={IconColor.ORANGE}
          size={6}
          tooltipText={t('BUY_BOX.CHAINFLEX_OVERSTOCK_SALE.INFO')}
          iconInline
        />
      </div>
      <div className="flex flex-wrap w-full gap-3">
        {overstockItems.map((item, index) => (
          <OverstockItem
            // eslint-disable-next-line react/no-array-index-key
            key={`${item.articleNo}-${item.chainflexMeterLength}-${index}`}
            item={item}
            selected={
              selectedChainflexOverstockItem?.articleNo === item.articleNo &&
              selectedChainflexOverstockItem?.storageLocation === item.storageLocation &&
              selectedChainflexOverstockItem?.chainflexMeterLength === item.chainflexMeterLength &&
              overstockItems.indexOf(selectedChainflexOverstockItem) === index
            }
          />
        ))}
      </div>
    </div>
  );
};
